import {$t} from "../../i18n";
import {getCustomFieldsByRelatedType} from "../../composables/use-custom-fields";
import {getters as AccountModule} from "../../store/modules/account";

const EXCLUDED_CUSTOM_FIELD_NAMES = {
    client: ["api_name", "api_key", "api_secret", "api_country", "color", "comments", "contract_settings.currency",
        "contract_settings.invoice_due_date_period", "contract_settings.invoice_recipients", "cp_email"],
    contact: ["cp_rights"]
}
const EXCLUDED_CUSTOM_FIELD_TYPES = {
    client: ["tax_id", "emails", "email", "roles", "contacts", 'cp_menu_links']
}

export const getHeaders = (accountId, relatedType, config) => {
    let headers = []

    const customFields = getCustomFieldsByRelatedType(accountId, [relatedType])
    const relatedObjectHeaders = getRelatedObjectHeaders(relatedType, config)
    const customFieldHeaders = getCustomFieldHeaders(relatedType, config, customFields)


    headers = [...relatedObjectHeaders, ...customFieldHeaders]

    headers = headers.sort((a, b) => {
        const locale_code = AccountModule.current_account.value.locale_code
        return a.text.localeCompare(b.text, locale_code.replace("_", "-"))
    })

    headers.push(ACTION_ELEMENT)
    return headers
}

const getRelatedObjectHeaders = (relatedType, config) => {
    const headers = []

    const currentHeaderMapper = HEADER_MAPPER[relatedType].filter(el => !el.hasCustomField)
    for (const mapperObject of currentHeaderMapper) {

        if (!mapperObject.fallbackTitle || !mapperObject.forcedValueLocation) {
            // Misconfigured HEADER_MAPPER !
            continue
        }
        const header = convertMapperToHeader(mapperObject)
        headers.push(header)
    }

    // Conditional
    const currentConditionalHeaderMapper = CONDITIONAL_HEADER_MAPPER[relatedType].filter(el => !el.hasCustomField)
    for (const mapperObject of currentConditionalHeaderMapper) {
        const isEnabled = config[mapperObject.conditionalType]
        if (isEnabled) {
            const header = convertMapperToHeader(mapperObject)
            headers.push(header)
        }
    }

    return headers
}

const getCustomFieldHeaders = (relatedType, config, customFields) => {
    let customFieldHeaders = []

    for (const customField of customFields) {
        if (isExcludedField(relatedType, customField) || !customField.is_visible) {
            continue
        }
        let fieldMapper = HEADER_MAPPER[relatedType].find(el => el.name === customField.name)
        if (!fieldMapper) {
            fieldMapper = BLANK_MAPPER_OBJ
            fieldMapper.fallbackTitle = customField.name
            fieldMapper.hasCustomField = true
        }

        customFieldHeaders.push(
            {
                text: customField.title || fieldMapper.fallbackTitle,
                value: fieldMapper.forcedValueLocation || customField.name,
                align: fieldMapper.align,
                class: fieldMapper.class,
                sortable: fieldMapper.sortable,
                default: fieldMapper.default,
                defaultSortOrder: fieldMapper.defaultSortOrder,
                hasCustomField: fieldMapper.hasCustomField,
                field: customField,
            }
        )
    }

    return customFieldHeaders
}

const isExcludedField = (relatedType, customField) => {
    const excludedFieldTypes = EXCLUDED_CUSTOM_FIELD_TYPES[relatedType] || []
    const excludedFieldNames = EXCLUDED_CUSTOM_FIELD_NAMES[relatedType] || []
    return excludedFieldTypes.includes(customField.type) || excludedFieldNames.includes(customField.name)
}

const convertMapperToHeader = (mapperObject) => {
    return {
        text: mapperObject.fallbackTitle,
        value: mapperObject.forcedValueLocation,
        class: mapperObject.class,
        align: mapperObject.align,
        sortable: mapperObject.sortable,
        default: mapperObject.default,
        defaultSortOrder: mapperObject.defaultSortOrder,
        hasCustomField: mapperObject.hasCustomField,
        field: null
    }
}

const ACTION_ELEMENT = {
    text: "",
    value: "action",
    align: "right pr-2",
    class: "text-no-wrap",
    sortable: false,
    removable: false,
    default: true,
    width: 0
}

const BLANK_MAPPER_OBJ = {
    fallbackTitle: "",
    name: null,
    align: "left",
    default: false,
    sortable: false,
    class: "",
    hasCustomField: null,
    forcedValueLocation: null,
    defaultSortOrder: 1_000_000
}

const HEADER_MAPPER = {
    client: [
        {
            fallbackTitle: $t("Client name"),
            name: "name",
            align: "left",
            default: true,
            sortable: true,
            class: "",
            hasCustomField: true,
            forcedValueLocation: null,
            defaultSortOrder: 0,
        },
        {
            fallbackTitle: $t("Client group"),
            name: "groups",
            align: "left",
            default: true,
            sortable: false,
            class: "",
            hasCustomField: true,
            forcedValueLocation: "groups_string",
            defaultSortOrder: 3,
        },
        {
            fallbackTitle: $t("Client code"),
            name: "code",
            align: "left",
            default: true,
            sortable: true,
            class: "text-no-wrap",
            hasCustomField: true,
            forcedValueLocation: "ext_client_id",
            defaultSortOrder: 4,
        },
        {
            fallbackTitle: $t("Primary user"),
            name: null,
            align: "left",
            default: true,
            sortable: true,
            class: "text-no-wrap",
            hasCustomField: false,
            forcedValueLocation: "default_person_name",
            defaultSortOrder: 1,
        },
        {
            fallbackTitle: $t("Added date"),
            ukuKey: null,
            align: "left",
            default: false,
            sortable: true,
            class: "text-no-wrap",
            hasCustomField: false,
            forcedValueLocation: "created_at",
            defaultSortOrder: 7,
        },
        {
            fallbackTitle: $t("Updated"),
            name: null,
            align: "left",
            default: false,
            sortable: false,
            class: "text-no-wrap",
            hasCustomField: false,
            forcedValueLocation: "updated_at",
            defaultSortOrder: 8,
        },
        {
            fallbackTitle: $t("Sub-companies"),
            name: null,
            align: "left",
            default: false,
            sortable: false,
            class: "text-no-wrap",
            hasCustomField: false,
            forcedValueLocation: "subclients",
            defaultSortOrder: 9,
        },
    ],
    account_member: [
        {fallbackTitle: "", value: "avatar", forcedValueLocation: "avatar", sortable: false, align: "left pl-3", width: 31, default: true, defaultSortOrder: 1},
        {fallbackTitle: $t("Name"), value: "name", forcedValueLocation: "name", default: true, defaultSortOrder: 2},
        {fallbackTitle: $t("Email"), value: "email", forcedValueLocation: "email", default: true, defaultSortOrder: 3},
        {fallbackTitle: $t("Role"), value: "role", forcedValueLocation: "role", default: true, defaultSortOrder: 4},
        {fallbackTitle: $t("Status"), value: "status", forcedValueLocation: "status", default: true, defaultSortOrder: 5},
        {fallbackTitle: $t("Clients"), sortable: false, value: "client_count", forcedValueLocation: "client_count", default: true, defaultSortOrder: 6},
        {fallbackTitle: "", value: "edit", sortable: false, align: "right", default: true, defaultSortOrder: 7},
        {fallbackTitle: $t("External ID"), sortable: false, value: "ext_user_id", forcedValueLocation: "ext_user_id", default: true, defaultSortOrder: 9}
    ]
}

const CONDITIONAL_HEADER_MAPPER = {
    client: [
        {
            conditionalType: "billing",
            fallbackTitle: $t("Contract count"),
            name: null,
            align: "left",
            default: true,
            sortable: false,
            class: "text-no-wrap",
            hasCustomField: false,
            forcedValueLocation: "contract_count",
            defaultSortOrder: 2,
        },
        {
            conditionalType: "billing",
            fallbackTitle: $t("Billing"),
            name: null,
            align: "left",
            default: true,
            sortable: false,
            class: "text-no-wrap",
            hasCustomField: false,
            forcedValueLocation: "active_contracts",
            defaultSortOrder: 5,
        },
        {
            conditionalType: "monitoring",
            fallbackTitle: $t("Monitoring"),
            name: null,
            align: "left",
            default: true,
            sortable: false,
            class: "text-no-wrap",
            hasCustomField: false,
            forcedValueLocation: "monitoring",
            defaultSortOrder: 6,
        },
        {
            conditionalType: "client_portal",
            fallbackTitle: $t("Client portal"),
            name: "is_cp_active",
            align: "left",
            default: false,
            sortable: false,
            class: "text-no-wrap",
            hasCustomField: true,
            forcedValueLocation: null,
            defaultSortOrder: 10,
        },
        {
            conditionalType: "integration",
            fallbackTitle: $t("Integration"),
            name: null,
            align: "right",
            default: true,
            sortable: false,
            class: "",
            hasCustomField: false,
            forcedValueLocation: "integration",
            defaultSortOrder: 11,
        },
        {
            conditionalType: "budgets",
            fallbackTitle: $t("Budgets"),
            name: null,
            align: "right",
            default: false,
            sortable: false,
            class: "",
            hasCustomField: false,
            forcedValueLocation: "budgets",
            defaultSortOrder: 12,
        },
        {
            conditionalType: "budgets",
            fallbackTitle: $t("Budget warning"),
            name: null,
            align: "right",
            default: false,
            sortable: false,
            class: "",
            hasCustomField: false,
            forcedValueLocation: "budget_warnings",
            defaultSortOrder: 13,
        }
    ],
    account_member: [{conditionalType: "is_office", text: $t("Office"), fallbackTitle: $t("Office"), sortable: true, value: "is_office", default: true, defaultSortOrder: 8, name: 'is_office', forcedValueLocation: "is_office"}]
}
